import {
  faCloudRain,
  faFilter,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@stormwater-software/lib-ui';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { goToDirections } from '../../../utils/googlemaps';
import xhrService from '../../../utils/xhrService';
import { useMapsContext, FilterOption } from '../../../contexts/MapsContext';
import AppContext from '../../../contexts/AppContext';
import Loading from '../../Shared/ResourceIndex/Loading';
import ReactSelectFilter from '../shared/SideFilterSharedSelect/SideFilterSharedSelect';
import Tooltip from '@mui/material/Tooltip';
import DashboardMapGoogle from './dashboardMap/DashboardMapGoogle';
import DashboardMapMapbox from './dashboardMap/DashboardMapMapbox';
import { isProperCoordinates } from './dashboardMap/mapShared';

/**
 * Which map layer to render. SW2-2247: "mapbox" is the fallback for the restricted
 * Google Cloud account; anything else, including unset, keeps the historic Google
 * layer. Reverting on reinstatement is this variable plus a rebuild — note that
 * REACT_APP_* values are baked in at build time, so changing it needs a redeploy, not
 * just a restart.
 */
const DashboardMapLayer =
  (process.env.REACT_APP_MAP_PROVIDER || '').toLowerCase().trim() === 'mapbox'
    ? DashboardMapMapbox
    : DashboardMapGoogle;

const ProjectInfoModal = ({
  data,
  onClose,
}: {
  data: any;
  onClose: () => void;
}) => {
  const [rainTab, setRainTab] = useState<'h24' | 'h48' | 'h72'>('h24');
  const [forecastTab, setForecastTab] = useState<'h24' | 'h48'>('h24');
  const [noaaForecast, setNoaaForecast] = useState<{
    total24hr: number | null;
    total48hr: number | null;
    prob24hr: number | null;
    prob48hr: number | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    setNoaaForecast(null);

    const set = (val: typeof noaaForecast) => {
      if (!cancelled) setNoaaForecast(val);
    };

    const useNoaaApi = () =>
      xhrService
        .get(`/api/weather/${data.latitude}/${data.longitude}/forecast48hr`)
        .then((r: any) => {
          const d = r.data || {};

          set({
            total24hr: d.total24hr ?? null,
            total48hr: d.total48hr ?? null,
            prob24hr: d.prob24hr ?? null,
            prob48hr: d.prob48hr ?? null,
          });
        })
        .catch(() =>
          set({
            total24hr: null,
            total48hr: null,
            prob24hr: null,
            prob48hr: null,
          }),
        );

    const today = new Date().toISOString().substring(0, 10);

    xhrService
      .get(`/api/projects/${data.id}/noaaWeatherTable?date=${today}`)
      .then((r: any) => {
        const rows: any[] = Array.isArray(r.data) ? r.data : [];

        if (rows.length > 0) {
          const sumInches = (n: number) =>
            Math.round(
              rows
                .slice(0, n)
                .reduce(
                  (acc: number, row: any) =>
                    acc + (parseFloat(row['6hr Precip (in)']) || 0),
                  0,
                ) * 100,
            ) / 100;
          const maxProb = (n: number) =>
            rows
              .slice(0, n)
              .reduce(
                (max: number, row: any) =>
                  Math.max(max, parseFloat(row['Chance of Precip. (%)']) || 0),
                0,
              );

          set({
            total24hr: sumInches(4),
            total48hr: sumInches(8),
            prob24hr: maxProb(4),
            prob48hr: maxProb(8),
          });

          return;
        }

        return useNoaaApi();
      })
      .catch(() => useNoaaApi());

    return () => {
      cancelled = true;
    };
  }, [data.id]);

  const formatDate = (d: string | null | undefined) => {
    if (!d) return null;
    const dt = new Date(d);

    if (isNaN(dt.getTime())) return null;

    return dt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const nextInspColor = (d: string | null | undefined): string => {
    if (!d) return '#333';
    const diff = (new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24);

    if (diff < 0) return '#c62828';
    if (diff <= 7) return '#e65100';

    return '#2e7d32';
  };

  const rainData: { date: string; amount: number }[] =
    data.rainAccumulation?.[rainTab] || [];
  const rainTotal =
    Math.round(rainData.reduce((sum, r) => sum + r.amount, 0) * 100) / 100;
  const rainDisplayData =
    rainTab === 'h24'
      ? rainData.length > 0
        ? [{ date: 'total', amount: rainTotal }]
        : []
      : rainData;
  const maxRain = Math.max(...rainDisplayData.map((r) => r.amount), 0.01);

  const formatRainLabel = (date: string) => {
    if (date === 'total') return 'Total';
    const dt = new Date(date + 'T12:00:00');

    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className='dashboard-map__modal-container'>
      <div className='dashboard-map__modal-card'>
        {/* Header */}
        <div
          style={{
            background: '#00897b',
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <h2
            style={{
              color: '#fff',
              margin: 0,
              fontSize: 14,
              fontWeight: 600,
              flex: 1,
              lineHeight: 1.3,
            }}
          >
            {data.projectName}
          </h2>
          <span
            style={{
              background: data.status === 'Active' ? '#e8f5e9' : '#fbe9e7',
              color: data.status === 'Active' ? '#2e7d32' : '#bf360c',
              borderRadius: 10,
              padding: '2px 8px',
              fontSize: 10,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {data.status}
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              padding: 2,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div
          style={{ padding: '12px 14px', overflowY: 'auto', maxHeight: 440 }}
        >
          {/* Info grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px 16px',
              marginBottom: 12,
            }}
          >
            {(
              [
                ['Client', data.clientName],
                ['Contact', data.primaryContactName],
                ['Inspector', data.inspectorName],
                ['Permit #', data.permittee],
                ['Location', data.location],
                ['Acres', data.currentAcreage],
              ] as [string, any][]
            ).map(([label, value], idx) => (
              <div key={idx}>
                <div
                  style={{
                    fontSize: 10,
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: 1,
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#333',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                  }}
                >
                  {value || 'N/A'}
                </div>
              </div>
            ))}
          </div>

          <hr
            style={{
              border: 'none',
              borderTop: '1px solid #eee',
              margin: '8px 0',
            }}
          />

          {/* Inspections */}
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#777',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 6,
              }}
            >
              Inspections
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '6px 16px',
              }}
            >
              <div>
                <div style={{ fontSize: 10, color: '#999' }}>
                  Last Inspection
                </div>
                <div
                  style={{ fontSize: 12, fontWeight: 600, color: '#2e7d32' }}
                >
                  {formatDate(data.lastInspectionDate) || 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#999' }}>
                  Next Inspection
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: nextInspColor(data.nextInspectionDate),
                  }}
                >
                  {formatDate(data.nextInspectionDate) || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          <hr
            style={{
              border: 'none',
              borderTop: '1px solid #eee',
              margin: '8px 0',
            }}
          />

          {/* Precipitation */}
          <div>
            {/* Forecast header row with inline toggle */}
            <div
              style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#777',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Forecast
              </div>
              <div style={{ display: 'flex', gap: 3, marginLeft: 'auto' }}>
                {(['h24', 'h48'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setForecastTab(tab)}
                    style={{
                      padding: '1px 6px',
                      fontSize: 9,
                      fontWeight: 600,
                      border: forecastTab === tab ? 'none' : '0.5px solid #999',
                      borderRadius: 3,
                      cursor: 'pointer',
                      background:
                        forecastTab === tab ? '#00897b' : 'transparent',
                      color: forecastTab === tab ? '#fff' : '#888',
                    }}
                  >
                    {tab === 'h24' ? '24hr' : '48hr'}
                  </button>
                ))}
              </div>
            </div>

            {/* Forecast block */}
            <div
              style={{
                background: '#E6F1FB',
                borderRadius: 6,
                padding: '8px 10px',
                marginBottom: 10,
              }}
            >
              {noaaForecast == null ? (
                <div style={{ fontSize: 11, color: '#1565c0' }}>…</div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FontAwesomeIcon
                    icon={faCloudRain}
                    style={{ color: '#1565c0', fontSize: 14 }}
                  />
                  <span
                    style={{ fontSize: 11, color: '#1565c0', fontWeight: 600 }}
                  >
                    {forecastTab === 'h24' ? '24hr forecast' : '48hr forecast'}
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 11, color: '#1565c0' }}>
                      {forecastTab === 'h24'
                        ? noaaForecast.prob24hr != null
                          ? `${noaaForecast.prob24hr}%`
                          : ''
                        : noaaForecast.prob48hr != null
                          ? `${noaaForecast.prob48hr}%`
                          : ''}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#1565c0',
                      }}
                    >
                      {forecastTab === 'h24'
                        ? noaaForecast.total24hr != null
                          ? `${noaaForecast.total24hr}"`
                          : 'N/A'
                        : noaaForecast.total48hr != null
                          ? `${noaaForecast.total48hr}"`
                          : 'N/A'}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* Historical header + tabs */}
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#777',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 6,
              }}
            >
              Historical
            </div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              {(['h24', 'h48', 'h72'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setRainTab(tab)}
                  style={{
                    flex: 1,
                    padding: '3px 0',
                    fontSize: 11,
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    background: rainTab === tab ? '#178FD8' : '#f0f0f0',
                    color: rainTab === tab ? '#fff' : '#666',
                  }}
                >
                  {tab === 'h24' ? '24hr' : tab === 'h48' ? '48hr' : '72hr'}
                </button>
              ))}
            </div>

            {rainDisplayData.length === 0 ? (
              <div
                style={{
                  fontSize: 11,
                  color: '#bbb',
                  textAlign: 'center',
                  padding: '6px 0',
                }}
              >
                No data
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {rainDisplayData.map((row) => (
                  <div
                    key={row.date}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                    }}
                  >
                    <div
                      style={{
                        width: 54,
                        flexShrink: 0,
                        color: '#666',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatRainLabel(row.date)}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        background: '#f0f0f0',
                        borderRadius: 3,
                        height: 11,
                      }}
                    >
                      <div
                        style={{
                          width: `${(row.amount / maxRain) * 100}%`,
                          background: '#178FD8',
                          height: '100%',
                          borderRadius: 3,
                          minWidth: row.amount > 0 ? 2 : 0,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: 34,
                        flexShrink: 0,
                        color: '#333',
                        fontWeight: 600,
                        textAlign: 'right',
                      }}
                    >
                      {row.amount}"
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div
          className='dashboard-map__modal-buttons'
          style={{ padding: '10px 14px', borderTop: '1px solid #eee' }}
        >
          <Link
            to={`/divisions/${data.divisionId}/projects/${data.id}/inspections`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <Button primary>View Project</Button>
          </Link>
          <Button
            primary
            onClick={() => goToDirections(data.latitude, data.longitude)}
          >
            Get Directions
          </Button>
        </div>
      </div>
    </div>
  );
};

const FilterMenu = ({ userRole }: { userRole?: string }) => {
  const [open, setOpen] = useState(false);
  // Tracks unsaved edits per filter, independent of which filter is shown on the map.
  const [editedFilters, setEditedFilters] = useState<{
    project: boolean;
    inspector: boolean;
  }>({
    project: false,
    inspector: false,
  });
  const {
    activeFilter,
    setActiveFilter,
    appliedFilter,
    selectedOptions,
    setSelectedOptions,
    projectFilterOptions,
    inspectorFilterOptions,
    refreshMapData,
    resetData,
  } = useMapsContext();

  // Save is shown when the active filter has unsaved edits, or when its selections
  // aren't the ones currently applied to the map (e.g. after the other filter was saved).
  const isSaveVisible =
    editedFilters[activeFilter as 'project' | 'inspector'] ||
    appliedFilter !== activeFilter;

  // A dependent dropdown is empty for one of two very different reasons, and the
  // message has to say which. Telling someone to select a division they have
  // already selected is worse than saying nothing — it reads as a broken filter.
  const emptyReason = (what: string) => {
    const hasDivision = (selectedOptions.divisions?.length ?? 0) > 0;
    const hasClient = (selectedOptions.clients?.length ?? 0) > 0;

    if (!hasDivision && !hasClient) return 'Select a division and client first';
    if (!hasDivision) return 'Select a division first';
    if (!hasClient) return 'Select a client first';

    return `No ${what} on the projects you can access for this client`;
  };

  const handleSelect = (selectedOptions: any, type: string) => {
    setSelectedOptions((prevState: any) => ({
      ...prevState,
      [type]: selectedOptions,
    }));
    setEditedFilters((prev) => ({
      ...prev,
      [activeFilter as 'project' | 'inspector']: true,
    }));
  };

  const handleSaveButtonClick = () => {
    refreshMapData();
    setEditedFilters((prev) => ({
      ...prev,
      [activeFilter as 'project' | 'inspector']: false,
    }));
  };

  const resetButtonHandler = () => {
    resetData(activeFilter);
    setEditedFilters((prev) => ({
      ...prev,
      [activeFilter as 'project' | 'inspector']: false,
    }));
  };

  const [inspectorSearch, setInspectorSearch] = useState('');

  const renderInspectorGroup = (
    label: string,
    options: FilterOption[],
    onShowTeam?: (managerId: string) => void,
  ) => {
    const filtered = options.filter((o) =>
      o.label?.toLowerCase().includes(inspectorSearch.toLowerCase()),
    );

    if (filtered.length === 0) return null;

    return (
      <div key={label} style={{ width: '100%' }}>
        <div
          className='inspector-group-label'
          style={{
            fontSize: '10px',
            fontWeight: 500,
            color: '#aaa',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            margin: '10px 0 4px',
            paddingLeft: '0',
            width: '100%',
          }}
        >
          {label}
        </div>
        {filtered.map((option) => {
          const isChecked = (selectedOptions.inspectors as string[]).includes(
            String(option.value),
          );

          return (
            <div
              key={option.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '3px 0',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flex: 1,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const current = selectedOptions.inspectors as string[];
                  const val = String(option.value);
                  const next = isChecked
                    ? current.filter((v) => v !== val)
                    : [...current, val];

                  handleSelect(next, 'inspectors');
                }}
              >
                <input
                  type='checkbox'
                  checked={isChecked}
                  onChange={() => {}}
                  style={{
                    width: '14px',
                    height: '14px',
                    minWidth: '14px',
                    maxWidth: '14px',
                    flexShrink: 0,
                    flexGrow: 0,
                    cursor: 'pointer',
                    accentColor: '#1D9E75',
                  }}
                />
                <div
                  style={{
                    fontSize: '12px',
                    color: '#ffffff',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    marginLeft: '4px',
                  }}
                >
                  {option.label}
                </div>
              </div>
              {onShowTeam && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowTeam(option.value);
                  }}
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    background: '#1D9E75',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Show Team
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const selfEntry = [
    ...inspectorFilterOptions.areaManagers,
    ...inspectorFilterOptions.inspectors,
  ].find((u) => u.isSelf);
  let myTeamIds: (string | number)[];

  if (selfEntry) {
    const selfIdStr = String(selfEntry.value);
    // Area managers who directly report to self (Regional Manager case)
    const directAMIds = inspectorFilterOptions.areaManagers
      .filter((am) => String(am.parentId) === selfIdStr)
      .map((am) => am.value);
    // Inspectors who report to self directly (Area Manager) or to self's direct AMs (Regional Manager)
    const teamInspectorIds = inspectorFilterOptions.inspectors
      .filter(
        (i) =>
          String(i.parentId) === selfIdStr ||
          directAMIds.some((amId) => String(i.parentId) === String(amId)),
      )
      .map((i) => i.value);

    myTeamIds = Array.from(
      new Set([selfEntry.value, ...directAMIds, ...teamInspectorIds]),
    );
  } else {
    // Admin / Super Admin — all listed users are their scope
    myTeamIds = Array.from(
      new Set([
        ...inspectorFilterOptions.areaManagers.map((am) => am.value),
        ...inspectorFilterOptions.inspectors.map((i) => i.value),
        ...(inspectorFilterOptions.unassignedInspectors || []).map(
          (i) => i.value,
        ),
      ]),
    );
  }
  const isMyTeamSelected =
    myTeamIds.length > 0 &&
    myTeamIds.every((id) =>
      (selectedOptions.inspectors as string[]).includes(String(id)),
    );

  return (
    <div className='dashboard-map'>
      <div className='dashboard-map__menu'>
        <div
          className='dashboard-map__cog-wrapper'
          onClick={() => setOpen(!open)}
        >
          <FontAwesomeIcon icon={faFilter} className='pad-left' />
        </div>

        <div
          className='dashboard-map__drawer-wrapper'
          style={{ display: open ? 'block' : 'none' }}
        >
          <div className='dashboard-map__drawer'>
            <section>
              <p>Map Filter</p>
              <hr />
              <div className='dashboard-map__drawer__divide-section'>
                <span
                  className={activeFilter === 'project' ? 'active' : ''}
                  onClick={() => {
                    setActiveFilter('project');
                  }}
                >
                  Project Filter
                </span>
                <span
                  className={activeFilter === 'inspector' ? 'active' : ''}
                  onClick={() => {
                    setActiveFilter('inspector');
                  }}
                  style={{
                    display: [
                      'Super Admin',
                      'Admin',
                      'Regional Manager',
                      'Area / Assistant Manager',
                    ].includes(userRole || '')
                      ? 'flex'
                      : 'none',
                  }}
                >
                  Inspector Filter
                </span>
              </div>
              <section
                style={{
                  display: activeFilter === 'project' ? 'block' : 'none',
                }}
              >
                <div className='dashboard-map__filter-dropdowns'>
                  {![
                    'Client User',
                    'Inspector',
                    'SWMP Admin',
                    'CDOT PE/Designee',
                    'Client Inspector',
                    'Supervising Engineer',
                    'Project Engineer',
                    'Regional Manager (External)',
                  ].includes(userRole || '') && (
                    <ReactSelectFilter
                      options={projectFilterOptions.regions}
                      isMulti={false}
                      value={selectedOptions.regions}
                      onChange={(value) => handleSelect(value, 'regions')}
                      menuPortalTarget={document.body}
                    />
                  )}
                  <ReactSelectFilter
                    options={projectFilterOptions.divisions}
                    isMulti={true}
                    value={selectedOptions.divisions}
                    onChange={(value) => handleSelect(value, 'divisions')}
                    placeholder='Divisions'
                    menuPortalTarget={document.body}
                  />
                  <ReactSelectFilter
                    options={projectFilterOptions.clients}
                    isMulti={true}
                    value={selectedOptions.clients}
                    onChange={(value) => handleSelect(value, 'clients')}
                    placeholder='Clients'
                    menuPortalTarget={document.body}
                  />
                  {/* `=== 0`, not `<= 1`: unlike `regions`, projectTypes has no
                      default 'all' entry, so `<= 1` disabled the filter for every
                      client with exactly one project type — 796 of them. */}
                  {projectFilterOptions.projectTypes.length === 0 ? (
                    <Tooltip title={emptyReason('project types')}>
                      <span>
                        <ReactSelectFilter
                          options={projectFilterOptions.projectTypes}
                          isMulti={true}
                          value={selectedOptions.projectTypes}
                          onChange={(value) =>
                            handleSelect(value, 'projectTypes')
                          }
                          placeholder='Project Types'
                          isDisabled={true}
                          noOptionsMessage={() => emptyReason('project types')}
                          menuPortalTarget={document.body}
                        />
                      </span>
                    </Tooltip>
                  ) : (
                    <ReactSelectFilter
                      options={projectFilterOptions.projectTypes}
                      isMulti={true}
                      value={selectedOptions.projectTypes}
                      onChange={(value) => handleSelect(value, 'projectTypes')}
                      placeholder='Project Types'
                      menuPortalTarget={document.body}
                    />
                  )}
                  {projectFilterOptions.projectContacts.length === 0 ? (
                    <Tooltip title={emptyReason('project contacts')}>
                      <span>
                        <ReactSelectFilter
                          options={projectFilterOptions.projectContacts}
                          isMulti={true}
                          value={selectedOptions.projectContacts}
                          onChange={(value) =>
                            handleSelect(value, 'projectContacts')
                          }
                          placeholder='Project Contacts'
                          isDisabled={true}
                          noOptionsMessage={() => emptyReason('project contacts')}
                          menuPortalTarget={document.body}
                        />
                      </span>
                    </Tooltip>
                  ) : (
                    <ReactSelectFilter
                      options={projectFilterOptions.projectContacts}
                      isMulti={true}
                      value={selectedOptions.projectContacts}
                      onChange={(value) =>
                        handleSelect(value, 'projectContacts')
                      }
                      placeholder='Project Contacts'
                      menuPortalTarget={document.body}
                    />
                  )}
                </div>
              </section>
              <section
                style={{
                  display: activeFilter === 'inspector' ? 'block' : 'none',
                }}
              >
                <div className='dashboard-map__filter-dropdowns'>
                  <ReactSelectFilter
                    options={inspectorFilterOptions.regions}
                    isMulti={false}
                    value={selectedOptions.regions}
                    onChange={(v) => handleSelect(v, 'regions')}
                    menuPortalTarget={document.body}
                  />
                  {['Regional Manager', 'Area / Assistant Manager'].includes(
                    userRole || '',
                  ) && (
                    <button
                      className='inspector-my-team-btn'
                      onClick={() => {
                        handleSelect(
                          isMyTeamSelected
                            ? []
                            : myTeamIds.map((id) => String(id)),
                          'inspectors',
                        );
                      }}
                      style={{
                        width: '100%',
                        padding: '6px',
                        marginBottom: '8px',
                        borderRadius: '4px',
                        border: '1px solid white',
                        color: 'white',
                        background: isMyTeamSelected
                          ? '#48af98'
                          : 'transparent',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      My Team
                    </button>
                  )}
                  <input
                    type='text'
                    placeholder='Search users...'
                    value={inspectorSearch}
                    onChange={(e) => setInspectorSearch(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '6px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#333',
                      background: 'white',
                    }}
                  />
                </div>
              </section>
            </section>
          </div>
          {activeFilter === 'inspector' && (
            <div
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '0 0 0 4px',
              }}
            >
              {renderInspectorGroup(
                'Regional Managers',
                inspectorFilterOptions.regionalManagers,
              )}
              {renderInspectorGroup(
                'Area Managers',
                inspectorFilterOptions.areaManagers,
                (managerId) => {
                  const managerIdStr = String(managerId);
                  const teamIds = inspectorFilterOptions.inspectors
                    .filter((i) => String(i.parentId) === managerIdStr)
                    .map((i) => i.value);
                  const current = selectedOptions.inspectors as string[];

                  handleSelect(
                    Array.from(new Set([...current, managerId, ...teamIds])),
                    'inspectors',
                  );
                },
              )}
              {renderInspectorGroup(
                'Inspectors',
                inspectorFilterOptions.inspectors,
              )}
              {renderInspectorGroup(
                'Unassigned Inspectors',
                inspectorFilterOptions.unassignedInspectors || [],
              )}
            </div>
          )}
          <div className='dashboard-map__action-buttons'>
            <Button
              primary
              style={{ visibility: isSaveVisible ? 'visible' : 'hidden' }}
              onClick={handleSaveButtonClick}
            >
              Save
            </Button>
            <button onClick={resetButtonHandler}>Reset Filter</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Maps = () => {
  const [showModal, setShowModal] = useState(false);
  const {
    mapData,
    activeFilter,
    selectedOptions,
    searchRef,
    handleSearchFunc,
    inspectorFilterOptions,
  } = useMapsContext();
  const [currentProject, setCurrentProject] = useState<any>(null);
  const [currentCoordinates, setCurrentCoordinates] = useState({
    lat: mapData[0]?.latitude || 45.51238,
    lng: mapData[0]?.longitude || -111.796459,
  });
  const [filterWiseHexColor, setFilterWiseHexColor] = useState<any>({});
  const [currentKey, setCurrentKey] = useState<string>('inspctorName');
  const appStore = useContext(AppContext);

  /**
   * A marker was clicked. Recentring here rather than in the layers is what keeps the
   * two implementations interchangeable — neither knows the modal or the centre exists.
   */
  const handleSelectProject = useCallback((project: any) => {
    setCurrentProject(project);
    setCurrentCoordinates({
      lat: Number(project.latitude),
      lng: Number(project.longitude),
    });
    setShowModal(true);
  }, []);

  useEffect(() => {
    setFilterWiseHexColor({});
    if (mapData.length > 0) {
      for (const item of mapData) {
        if (
          isProperCoordinates(Number(item.latitude), Number(item.longitude))
        ) {
          setCurrentCoordinates({
            lat: Number(item.latitude),
            lng: Number(item.longitude),
          });
          break;
        }
      }

      if (activeFilter === 'inspector') {
        setCurrentKey('inspectorName');
        const colors: any = {};
        let firstAssigned = false;

        mapData.forEach((item) => {
          if (item.inspectorName) {
            if (!colors[item.inspectorName]) {
              if (!firstAssigned) {
                colors[item.inspectorName] = '#DB4437';
                firstAssigned = true;
              } else {
                let color;

                do {
                  color = Math.floor(Math.random() * 16777215)
                    .toString(16)
                    .padStart(6, '0');
                } while (isTooDark(color));
                colors[item.inspectorName] = `#${color}`;
              }
            }
          }
        });

        setFilterWiseHexColor(colors);
      } else {
        const colors: any = {};
        let firstAssigned = false;

        // Priority-based filter key (assumes all are arrays)
        let filterKey = '';

        if (
          selectedOptions.projectContacts &&
          selectedOptions.projectContacts.length > 0
        ) {
          filterKey = 'primaryContactName';
        } else if (
          selectedOptions.projectTypes &&
          selectedOptions.projectTypes.length > 0
        ) {
          filterKey = 'projectTypeName';
        } else if (
          selectedOptions.clients &&
          selectedOptions.clients.length > 0
        ) {
          filterKey = 'clientName';
        } else if (
          selectedOptions.divisions &&
          selectedOptions.divisions.length > 0
        ) {
          filterKey = 'divisionName';
        }

        if (!filterKey) {
          setFilterWiseHexColor({});

          return;
        }

        setCurrentKey(filterKey);
        mapData.forEach((item) => {
          const key = item[filterKey];

          if (key) {
            if (!colors[key]) {
              if (!firstAssigned) {
                colors[key] = '#DB4437';
                firstAssigned = true;
              } else {
                let color;

                do {
                  color = Math.floor(Math.random() * 16777215)
                    .toString(16)
                    .padStart(6, '0');
                } while (isTooDark(color));
                colors[key] = `#${color}`;
              }
            }
          }
        });

        setFilterWiseHexColor(colors);
      }
    }
  }, [mapData]);


  function isTooDark(hex: string): boolean {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000; // luminance formula

    return brightness < 50; // tweak threshold (50 = very dark)
  }

  if (!appStore) return <Loading />;

  return (
    <div className='dashboard-map'>
      <FilterMenu userRole={appStore.get('user.roleName')} />
      <input
        className='dashboard-map__search-bar'
        ref={searchRef}
        onChange={handleSearchFunc}
        placeholder='Search'
      />

      <div className='dashboard-map__content'>
        <DashboardMapLayer
          mapData={mapData}
          filterWiseHexColor={filterWiseHexColor}
          currentKey={currentKey}
          center={currentCoordinates}
          onSelectProject={handleSelectProject}
        />
      </div>
      {Object.keys(filterWiseHexColor).length > 0 &&
        (() => {
          const renderGroup = (
            groupLabel: string,
            entries: [string, unknown][],
          ) =>
            entries.length === 0 ? null : (
              <div
                key={groupLabel}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '6px 16px',
                }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#888',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {groupLabel}
                </span>
                {entries.map(([label, color]) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: color as string,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#333',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            );

          if (activeFilter === 'inspector') {
            const managerNames = new Set(
              inspectorFilterOptions.areaManagers.map(
                (m: FilterOption) => m.label,
              ),
            );
            const regionalManagerNames = new Set(
              inspectorFilterOptions.regionalManagers.map(
                (m: FilterOption) => m.label,
              ),
            );
            const managerEntries = Object.entries(filterWiseHexColor)
              .filter(
                ([label]) =>
                  managerNames.has(label) || regionalManagerNames.has(label),
              )
              .sort(([a], [b]) => a.localeCompare(b));
            const inspectorEntries = Object.entries(filterWiseHexColor)
              .filter(
                ([label]) =>
                  !managerNames.has(label) && !regionalManagerNames.has(label),
              )
              .sort(([a], [b]) => a.localeCompare(b));

            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  padding: '4px 4px 4px',
                }}
              >
                {renderGroup('Managers', managerEntries)}
                {renderGroup('Inspectors', inspectorEntries)}
              </div>
            );
          }

          const projectGroupLabel: Record<string, string> = {
            primaryContactName: 'Project Contacts',
            projectTypeName: 'Project Types',
            clientName: 'Clients',
            divisionName: 'Divisions',
          };
          const groupLabel = projectGroupLabel[currentKey] || 'Projects';
          const entries = Object.entries(filterWiseHexColor).sort(([a], [b]) =>
            a.localeCompare(b),
          );

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                padding: '4px 4px 4px',
              }}
            >
              {renderGroup(groupLabel, entries)}
            </div>
          );
        })()}
      {showModal && (
        <ProjectInfoModal
          onClose={() => setShowModal(false)}
          data={currentProject}
        />
      )}
    </div>
  );
};

export default Maps;
